import * as Enumerable from 'linq';

import { Source } from '../../enums';
import { Entity, ShareableEntity } from '../entity';
import { ActivityEntityName } from './activity.entity.name';

export enum LabelType {
    Job = 'Job',
    Quote = 'Quote',
    Customer = 'Customer',
    User = 'User',
    Overheads = 'Overheads',
    KnowledgeBase = 'Knowledge Base',
    Form = 'Form',
    Finance = 'Finance',
    System = 'System',
    WorkOrderSystem = 'WorkOrderSystem',
    Account = 'Account',
    Contact = 'Contact',
    Zone = 'Zone',
    WorkCategory = 'Work Categories',
    LeadSource = 'Lead Source',
    Lead = 'Lead',
    Opportunity = 'Opportunity',
    OpportunityLost = 'Opportunity Lost',
    Project = 'Project',
    ProjectType = 'Project Type',
    PaymentType = 'Payment Type',
    File = 'File',
    Asset = 'Asset',
    ItemLocation = 'Item Location',
    Task = 'Task',
    Phase = 'Phase',
    Site = 'Site',
    Scheduling = 'Scheduling',
    ServiceType = 'Service Type',
    Subscriptions = 'Subscriptions',
    Agreement = 'Agreement',
    Work = 'Work',
}

export enum AlertPriority {
    Critical = 'Critical',
    High = 'High',
    Medium = 'Medium',
    Low = 'Low'
}

export interface AlertOptions {
    enable: boolean;
    icon?: string;
    priority?: AlertPriority;
    message?: string;
    allowCustomNote?: boolean;
    alertDisplay?: DisplayOptions;
    entitiesDisplay?: ActivityEntityName[];
}

export interface DisplayOptions {
    showInFieldApp: boolean;
    showInClientPortal: boolean;
    showInContractorPortal: boolean;
}

export interface Label extends LabelEntity {
    alert?: AlertOptions;
    display?: DisplayOptions;
}

export interface ServiceTypeTag extends Label {
    data?: {
        /** Only allow single service - can only add this service to a site or asset once. */
        multipleInstances?: boolean
    }
}


export interface LabelEntity extends ShareableEntity {
    icon?: string;
    type: LabelType;
}


export class SystemLabels {

    /** This appears for Jobs and WorkOrders that are participating in claims. In upstream tenancy (eg. Asset) this will be on the WorkOrder and for downstream (Contractor i.e Alectrix) this will be on the job. All claim tags are mutually exclusive i.e none or only 1 */
    public static claimPending: Label = { _id: '00000000-0000-0000-0000-00000000003', name: 'Claim Pending', type: LabelType.WorkOrderSystem };

    /** This label will be set on the Job when a downstream contractor (Alextrix) submits a claim upstream (Assett). All claim tags are mutually exclusive i.e none or only 1*/
    public static claimSubmitted: Label = { _id: '00000000-0000-0000-0000-00000000004', name: 'Claim Submitted', type: LabelType.WorkOrderSystem };

    /** This label will be on the WorkOrder for upstream tenancy ie. This occurs when a downstream tenancy (Alextrix) or a contractor using the portal submits a claim. All claim tags are mutually exclusive i.e none or only 1  */
    public static claimReceived: Label = { _id: '00000000-0000-0000-0000-00000000007', name: 'Claim Received', type: LabelType.WorkOrderSystem };

    /** This label will be on the WorkOrder when an upstream contractor (Assett) approves a claim. It will also appear on the Job for a downstream tenancy (Alectrix)  All claim tags are mutually exclusive i.e none or only 1*/
    public static claimApproved: Label = { _id: '00000000-0000-0000-0000-00000000005', name: 'Claim Approved', type: LabelType.WorkOrderSystem };

    /** This label will be on the WorkOrder when an upstream contractor (Assett) rejects a claim. It will also appear on the Job for a downstream tenancy (Alectrix)  All claim tags are mutually exclusive i.e none or only 1*/
    public static claimReturned: Label = { _id: '00000000-0000-0000-0000-00000000006', name: 'Claim Returned', type: LabelType.WorkOrderSystem };

    public static jobBulkInvoiceLocked: Label = { _id: '00000000-0000-0000-0000-00000000007', name: 'Bulk Invoice Processing', type: LabelType.Job };

    public static tenant = {
        //This unassigned is different to the contractor unassigned (i.e. not even assigned to a contractor)
        unassignedWorkOrder: { _id: '00000000-0000-0000-0000-00000000001', name: 'Unassigned', type: LabelType.WorkOrderSystem } as Label,
        claimPending: this.claimPending,
        claimSubmitted: this.claimSubmitted,
        claimApproved: this.claimApproved,
        claimReturned: this.claimReturned,
        claimReceived: this.claimReceived
    };

    public static contractor = {
        //This unassigned is different to the tenant unassigned (i.e. its been assigned to a contractor but we dont have a login contact on it yet, only relevant in portal)
        unassignedWorkOrder: { _id: '00000000-0000-0000-0000-00000000002', name: 'Unassigned', type: LabelType.WorkOrderSystem, showOn: [Source.ContractorPortal] } as Label,
        claimPending: this.claimPending,
        claimSubmitted: this.claimSubmitted,
        claimReceived: this.claimReceived, //Don't remove this, this is used in the contractor portal
        claimApproved: this.claimApproved,
        claimReturned: this.claimReturned
    }

    public static claimLabels = [
        this.claimPending,
        this.claimSubmitted,
        this.claimReceived,
        this.claimApproved,
        this.claimReturned
    ]

    public static claimLabelMap = new Map<string, Label>(this.claimLabels.map(x => [x._id, x]));

    public static client = {};

    public static isVisible(entity: Entity, destination: Source): boolean {

        if (!entity || !destination) return false;

        switch (destination) {
            case Source.Web: {
                return Enumerable
                    .from(SystemLabels.tenant)
                    .any(x => (x.value as Label)?._id === entity._id);
            }

            case Source.ContractorPortal: {
                return Enumerable
                    .from(SystemLabels.contractor)
                    .any(x => (x.value as Label)?._id === entity._id);
            }

            default:
                return false;
        }
    }

    public static forDestination(labels: Entity[], destination: Source) {
        return Enumerable
            .from(labels)
            .where(l => SystemLabels.isVisible(l, destination))
            .toArray();
    }

}

export const getClaimActivityName = (label: Entity, upstream: boolean) => {

    let labels: Record<string, string> = null;

    if (upstream) {

        labels = {
            [SystemLabels.claimReceived._id]: `Claim was received from contractor`,
            [SystemLabels.claimApproved._id]: `Claim was approved`,
            [SystemLabels.claimReturned._id]: `Claim was returned to contractor`,
        };

    } else {

        labels = {
            [SystemLabels.claimSubmitted._id]: `Claim was submitted`,
            [SystemLabels.claimApproved._id]: `Claim was approved`,
            [SystemLabels.claimReturned._id]: `Claim was returned for rework`,
        }

    }

    return labels[label._id];
}